import { useMemo, useState } from 'react';
import type { RelatedRequirement } from '@sr-test-wizard/shared';
import { JiraKeyLink } from '../../components/JiraKeyLink';
import { filterRequirementsForJurisdictions } from '../../lib/jurisdictionMatch';
import { useRunStore } from '../../state/runStore';

interface Props {
  requirements: RelatedRequirement[];
}

function RequirementRow({
  req,
  highlight,
}: {
  req: RelatedRequirement;
  highlight: boolean;
}) {
  const [open, setOpen] = useState(false);
  const hasDetails =
    (req.description && req.description.trim().length > 0) ||
    (req.labels && req.labels.length > 0);
  return (
    <div
      className="related-req-card"
      style={highlight ? undefined : { opacity: 0.55 }}
    >
      <button
        type="button"
        className="related-req-summary"
        onClick={() => hasDetails && setOpen((v) => !v)}
        aria-expanded={open}
        title={
          hasDetails
            ? open
              ? 'Hide details'
              : 'Show description and labels'
            : 'No description or labels on this requirement'
        }
        disabled={!hasDetails}
      >
        <span
          className="related-req-caret"
          aria-hidden
          style={{
            display: 'inline-block',
            width: 12,
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.12s ease',
            opacity: hasDetails ? 1 : 0.25,
          }}
        >
          {'\u25B6'}
        </span>
        <JiraKeyLink keyId={req.key} className="badge mono small linked-item-key" />
        <span className="related-req-title" title={req.summary}>
          {req.summary || '(no summary)'}
        </span>
        {req.jurisdictions.length > 0 && (
          <span
            className="row"
            style={{ flexWrap: 'wrap', gap: 4 }}
            title="Jurisdictions"
          >
            {req.jurisdictions.map((j) => (
              <span key={j} className="badge small">
                {j}
              </span>
            ))}
          </span>
        )}
      </button>
      {open && hasDetails && (
        <div className="related-req-details">
          {req.labels.length > 0 && (
            <div
              className="row"
              style={{ flexWrap: 'wrap', gap: 4, marginBottom: 8 }}
            >
              {req.labels.map((l) => (
                <span key={l} className="badge small">
                  {l}
                </span>
              ))}
            </div>
          )}
          {req.description && req.description.trim().length > 0 ? (
            <pre className="related-req-description">{req.description}</pre>
          ) : (
            <p className="muted small" style={{ margin: 0 }}>
              (no description on Jira)
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Renders the legal/regulatory REQ-* items linked to a TLIB, filtered to the
 * jurisdictions declared on the current TEXEC (with GE -> UK aliasing). Reads
 * the cached TEXEC view from the run store so this section reacts to Phase 1
 * refreshes without prop-drilling.
 *
 * Visibility rules:
 *   - 0 linked REQs at all   -> hide the section entirely (nothing to say).
 *   - filter hides them all  -> show the heading with a "0 of N match"
 *                               caption + a "Show all" toggle so the user
 *                               can verify what's actually linked and decide
 *                               whether the alias map needs an update.
 *   - some matches           -> show the matches; toggling "Show all"
 *                               reveals the rest at lowered opacity.
 *
 * Each row is collapsed by default and expands to show the requirement's
 * Jira description and labels.
 */
export function RelatedRequirementsSection({ requirements }: Props) {
  const texec = useRunStore((s) => s.texec);
  const codes = useMemo(() => {
    if (!texec) return [] as string[];
    const merged = [
      ...(texec.header.testedJurisdictions ?? []),
      ...(texec.reportData.jurisdictions ?? []),
    ];
    return Array.from(new Set(merged.filter(Boolean)));
  }, [texec]);

  const filtered = useMemo(
    () => filterRequirementsForJurisdictions(requirements, codes),
    [requirements, codes],
  );

  const [showAll, setShowAll] = useState(false);

  const total = requirements.length;
  if (total === 0) return null;

  const filteredKeys = new Set(filtered.map((r) => r.key));
  const hiddenByFilter = total - filtered.length;
  const hasFilter = codes.length > 0;
  const filterLabel = hasFilter ? codes.join(', ') : 'no jurisdictions set';

  const captionParts: string[] = [`${filtered.length} of ${total} match`];
  if (hasFilter) captionParts.push(`filter: ${filterLabel}`);
  if (hiddenByFilter > 0 && !showAll) {
    captionParts.push(`${hiddenByFilter} hidden`);
  }

  const rowsToRender = showAll ? requirements : filtered;

  return (
    <>
      <h3 style={{ marginBottom: 4 }}>Related requirements</h3>
      <div className="row" style={{ marginBottom: 8 }}>
        <span className="muted small">{captionParts.join(' \u00b7 ')}</span>
        <span className="spacer" />
        {hiddenByFilter > 0 && (
          <button
            type="button"
            className="btn btn-ghost small"
            onClick={() => setShowAll((v) => !v)}
            title={
              showAll
                ? 'Hide requirements that don\u2019t match the current jurisdictions'
                : 'Show every linked requirement, including those filtered out'
            }
          >
            {showAll ? 'Hide non-matching' : `Show all (${total})`}
          </button>
        )}
      </div>
      {filtered.length === 0 && !showAll && (
        <p className="muted small" style={{ marginTop: 0 }}>
          {total} requirement{total === 1 ? '' : 's'} linked, but none match the
          current TEXEC jurisdictions ({filterLabel}). Click "Show all" to see
          them anyway.
        </p>
      )}
      {rowsToRender.length > 0 && (
        <div className="related-req-list">
          {rowsToRender.map((req) => (
            <RequirementRow
              key={req.key}
              req={req}
              highlight={!showAll || filteredKeys.has(req.key)}
            />
          ))}
        </div>
      )}
    </>
  );
}
